'use client';

import React, { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';

import { useAuthContext } from '@/components/Providers/AuthProvider';
import { useAppContext } from '@/layouts/AppProvider';
import { useGetRoqLogicSilentLoginUrlQuery } from '@/store/api/appSettings';

interface RoqLogicLibraryContextProps {
  libraryUrl: string | null;
  isLoading: boolean;
  handleLibraryClick: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>) => void;
}

interface RoqLogicLibraryProviderProps {
  children: ReactNode;
}

const RoqLogicLibraryContext = createContext<RoqLogicLibraryContextProps>({
  libraryUrl: null,
  isLoading: false,
  handleLibraryClick: () => {},
});

const RoqLogicLibraryProvider = ({ children }: RoqLogicLibraryProviderProps) => {
  const { isLoggedIn } = useAuthContext();
  const { isBackOffice } = useAppContext();

  const shouldFetch = isLoggedIn && isBackOffice;

  const { data, isLoading } = useGetRoqLogicSilentLoginUrlQuery(undefined, {
    skip: !shouldFetch,
  });

  const libraryUrl = useMemo(() => {
    if (data?.success && data?.data) {
      return data.data;
    }
    return null;
  }, [data]);

  const handleLibraryClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();

      if (libraryUrl) {
        // Redirect to the API-returned URL on the same tab
        window.location.href = libraryUrl;
      } else {
        // If API returns false or no URL, redirect to homepage
        window.location.href = '/';
      }
    },
    [libraryUrl]
  );

  const value = useMemo(
    () => ({
      libraryUrl,
      isLoading,
      handleLibraryClick,
    }),
    [libraryUrl, isLoading, handleLibraryClick]
  );

  return <RoqLogicLibraryContext.Provider value={value}>{children}</RoqLogicLibraryContext.Provider>;
};

export const useRoqLogicLibrary = () => useContext(RoqLogicLibraryContext);
export default RoqLogicLibraryProvider;
